/* ════════════════════════════════════════════════════════════════════════ */
/* #region(collapsed) Documentation */
/**
  @license
  # COPYRIGHT NOTICE
    © CROUIN 2020-2025. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  # INTRODUCTION
    Code powered by MaxDesign.
    This modal uses HTML custom element modal-controller-maxdesign and modal-maxdesign.
    Custom elements extend HTML by creating new elements that carry their own JavaScript for adding new behavior.
    All methods can be accessed with the javascript Class MaxDesign (see "popular javascript methods" below).
 
  # POPULAR JAVASCRIPT METHODS
    const modal = MaxDesign.modal; // get modal object
    const controller = modal.controller; // get modal controller (custom element)
    const cart = modal.cart; // get custom element modal by data-id (cart, localization, ...)
    const modals = controller.modals; // get list of modals in DOM
    modal.cart.open(); // open cart modal
    modal['custom-id'].close(); // close custom modal with data-id 'custom-id'
    document.addEventListener('maxdesign:modal:open|close', e => console.log(`opening|closing modal: ${e.detail.modal}`)); // run code on modal open event
 
  # MORE INFORMATION
    Contact us at admin(at)maxdesign.expert
*/
/* #endregion ═════════════════════════════════════════════════════════════ */

window.MaxDesign = window.MaxDesign || class {};
MaxDesign.modal = new Proxy(
  {},
  {
    get: (_, prop) => document.querySelector(prop === 'controller' ? '#mcmd' : `.mmd[data-id="${prop}"]`),
  }
);

/* ════════════════════════════════════════════════════════════════════════ */
/* #region(collapsed) Modals Controller */
class ModalController extends HTMLElement {
  connectedCallback() {
    this.template = window['modal-template-maxdesign'];
    this.settings = this.json(this);
    this.modals = new Set();
    this.triggers = {
      cart: this.settings.find(block => block.cart_triggers)?.cart_triggers,
      editor: this.settings.map(({ heading }) => `a[href="${heading}" i]`).join(),
      custom: '[data-modal-trigger], a[href="#quiz" i], a[href="#localization" i]',
    };
    document.addEventListener('click', this.onDocumentClick.bind(this), { capture: true });
  }

  /**
   * Check conditions to trigger modal
   * @param {Object} e - click event
   */
  onDocumentClick(e) {
    this.onModalClose(e);
    this.onModalOpen(e);
    this.onModalPersist(e);
  }

  /**
   * Detect close modal event
   * Close when clicking out
   * Close when clicking on flex stretch area (as long as it's not hosting an open modal)
   * Close when clicking on data-close attr
   * Exclude composedPath first item because modal component stretches in mobile
   * We check for [data-modal-persist] in case we interact with an element outside the modal
   * @param {Object} e - target event
   */
  onModalClose(e) {
    const composedPath = e.composedPath();
    [...this.modals]
      .filter(modal => modal.hasAttribute('open'))
      .forEach(modal => {
        const clickOut = !composedPath.includes(modal) && composedPath[0] !== document.body;
        const clickStretchedArea = composedPath[0] == modal && !(modal.getAttribute('data-modal') == 'visible');
        const clickClose = composedPath.some(
          node =>
            node instanceof Element &&
            node.hasAttribute('data-close') &&
            (node.getRootNode().host == modal || node.closest('.mmd') == modal)
        );
        if ((clickOut || clickStretchedArea || clickClose) && !e.target.closest('[data-modal-persist]')) {
          e.preventDefault();
          modal.close();
        }
      });
  }

  /**
   * Detect open modal event
   * @param {Object} e - target event
   */
  onModalOpen(e) {
    const target = e.composedPath()[0];
    Object.entries(this.triggers).some(([type, selectors]) => {
      const trigger = target.matches(selectors) ? target : target.closest(selectors);
      if (trigger) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.getModal(type, trigger);
        return true;
      }
    });
  }

  /**
   * Add modal persistence on click to external elements
   * @param {Object} e - target event
   */
  onModalPersist(e) {
    const persistQuery = e.target.getAttribute('data-modal-persist');
    if (persistQuery) document.querySelector(persistQuery).dataset.modalPersist = '';
  }

  /**
   * Each modal type has its own set of criterias
   * RequestAnimationFrame + timeout trick https://stackoverflow.com/a/54228987/10294974
   * @param {string} type - editor, cart or custom
   * @param {HTMLObject} trigger - clicked element that is a modal trigger
   */
  async getModal(type, trigger) {
    let modal;
    switch (type) {
      case 'editor': {
        const container = trigger.closest('.mmd') || this;
        const frame = trigger.closest('.mmd') || document.documentElement;
        const obj = this.settings.find(
          ({ heading }) => heading && encodeURI(heading.trim()) === `#${trigger.href.split('#')?.[1]}`
        );
        const modalEl = MaxDesign.modal[encodeURI(obj?.heading?.trim())];
        modal = modalEl || (await this.buildModal(obj, obj?.heading, frame));
        if (modalEl?.parentElement !== container) container.appendChild(modal);
        requestAnimationFrame(() => setTimeout(() => modal.open(), 25));
        break;
      }
      case 'cart': {
        modal = MaxDesign.modal.cart;
        const { UI, controller } = MaxDesign?.cart;
        if (UI) modal.open();
        else if (controller) {
          modal.overlay = { state: 'loading' };
          controller.render(await controller.fetch());
          modal.open();
        } else location.href = trigger.href;
        break;
      }
      default: {
        const dataset = trigger.dataset;
        modal = MaxDesign.modal[dataset.modalTrigger] || MaxDesign.modal[`#${trigger?.href?.split('#')?.[1]}`];
        if (modal) {
          modal.data = dataset;
          modal.open();
        }
      }
    }
  }

  /**
   * Build editor modal on request
   * @param {Object} obj - block settings of current modal
   * @param {String} id - id of modal (based on heading)
   * @param {Object} frame - either documentElement or a modal parent
   * @returns modal custom element
   */
  async buildModal(obj, id, frame) {
    const modal = document.createElement('modal-maxdesign');
    modal.overlay = { state: 'loading', frame };
    modal.classList.add('mmd');
    modal.dataset.id = id;
    modal.slot = frame instanceof customElements.get('modal-maxdesign') ? 'modal' : '';
    modal.style.setProperty('--mmd-padding-mobile', `${obj.padding_mobile}`);
    modal.style.setProperty('--mmd-height-mobile', `${obj.height_mobile}%`);
    modal.style.setProperty('--mmd-padding-desktop', `${obj.padding_desktop}`);
    modal.style.setProperty('--mmd-width-desktop', `${obj.modal_width}`);
    const content = obj.liquid ? obj.liquid : obj.url ? await this.fetchPage(obj.url) : obj.richtext;
    modal.innerHTML = `
      <div slot="img" class="mmd__img">${obj.img ? `<img src="${obj.img}&width=100">` : ''}</div>
      <div slot="title" class="mmd__title">${obj.title}</div>
      <div slot="content" class="mmd__content">${content}</div>`;
    // we load first image for UX purposes
    const img = modal.querySelector('.mmd__content img');
    if (img) {
      img.loading = 'eager';
      await new Promise(resolve => {
        const timeout = setTimeout(resolve, 1000);
        img.complete
          ? resolve()
          : (img.onload = img.onerror =
              () => {
                clearTimeout(timeout);
                resolve();
              });
      });
    }
    return modal;
  }

  /**
   * Fetch a page asynchronously
   * @param {String} handle - page url handle
   * @returns page body
   */
  async fetchPage(handle) {
    try {
      const response = await fetch(handle + '.json');
      const data = await response.json();
      const json = Object.values(data)[0];
      const html = json.body_html || json.body || json.description;
      return html?.replace(/<img\s+src=/g, '<img loading="lazy" src=');
    } catch (message) {
      console.error(message);
      location.href = handle;
    }
  }

  get json() {
    return scope => JSON.parse(scope.querySelector(':scope > [type="application/json"]')?.innerText || '{}');
  }
}
/* #endregion ═════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════════════ */
/* #region(collapsed) Modal Elements */
class Modal extends HTMLElement {
  attributeChangedCallback(name, oldVal, newVal) {
    if (name !== 'open' || oldVal === newVal || !this.isConnected) return;
    newVal !== null ? this.#onOpen() : this.#onClose();
  }

  connectedCallback() {
    this.controller = MaxDesign.modal.controller;
    this.controller.modals.add(this);
    this.root = this.shadow ? this.attachShadow({ mode: 'open' }) : this;
    this.shadow && this.root.appendChild(this.controller.template.content.cloneNode(true));
    this.settings = this.controller.json(this);
    this.container = this.root.querySelector('[part="container"]') || this.root;
    this.component = this.container.firstElementChild?.assignedElements()?.[0] || this.container;
    this._frame = this.frame;
    this.isMobile && this.onSwipeToClose();
    this.hasAttribute('open') && this.#onOpen();
  }

  disconnectedCallback() {
    this.controller.modals.delete(this);
    this.#onClose(); /* useful for cart nested modals */
  }

  open() {
    this.setAttribute('open', '');
  }

  close() {
    this.removeAttribute('open');
  }

  async #onOpen() {
    await this.loadResources();
    this.overlay = { state: 'visible' };
    this.event = 'open';
  }

  #onClose() {
    this.overlay = { state: 'hidden' };
    this.event = 'close';
  }

  /**
   * Load modal resources asynchronously if there are any
   * We may also load and append a section in the modal
   * Script and stylesheet can be added in script/json { "resources": { "script": "...", "stylesheet": "..." } }
   */
  async loadResources() {
    const { resources, section } = this.settings || {};
    if ((!resources && !section) || this.assets == 'loaded') return;
    this.overlay = { state: 'loading' };
    const promises = [];

    const loadSection = async ({ selector, page }) => {
      try {
        const sectionId = document.querySelector(selector)?.id?.slice(16);
        if (!sectionId) throw new Error(`Section id for ${selector} not found`);
        const response = await fetch(
          `${Shopify.routes.root}${handle.replace(/^\/|\/$/g, '')}/?section_id=${sectionId}`
        );
        if (!response.ok) throw new Error(`Failed to load section: ${response}`);
        const result = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector(selector);
        if (result) {
          this.component.innerHTML = result.outerHTML;
          this.event = 'section:ready';
        }
      } catch (err) {
        console.error('Error loading section:', err);
      }
    };

    const loadResource = (type, attributes) =>
      new Promise(resolve => {
        this.appendChild(
          Object.assign(document.createElement(type), attributes, {
            onload: () => {
              this.event = `${type}:ready`;
              resolve();
            },
          })
        );
      });

    if (section?.page && section?.selector) promises.push(loadSection(section));
    if (resources?.stylesheet) promises.push(loadResource('link', { rel: 'stylesheet', href: resources.stylesheet }));
    if (resources?.script) promises.push(loadResource('script', { src: resources.script }));

    try {
      await Promise.race([
        Promise.all(promises),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Failed to load within 5 seconds')), 5000)),
      ]);
      this.assets = 'loaded';
    } catch (error) {
      console.error(error);
      this.close();
    }
  }

  /**
   * Swipe down in mobile to close modal
   */
  onSwipeToClose() {
    const header = this.root.querySelector('[part="header"]');
    const container = this.root.querySelector('[part="container"]');
    if (!header || !container) return;

    // Config
    const TAKEOVER = 6; // px before we treat as downward intent from container
    const RESIST = 60; // px before resistance starts
    const CLOSE_DIST = 140; // px to close
    const CLOSE_VEL = 1100; // px/s
    const TOP_COOLDOWN = 300; // ms prevent accidental close

    // State
    let dragging = false;
    let origin = null; // 'header' | 'container'
    let committed = false;
    let hintClosing = false;
    let dragY = 0;
    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    let raf = 0;
    let lastScrollTime = 0;
    let lastScrollTop = container.scrollTop;

    const atTop = () => container.scrollTop <= 0;

    const setY = y => {
      dragY = Math.max(0, y);
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.style.setProperty('--mmd-y', `${dragY}px`));
    };

    const updateVelocity = y => {
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      const v = ((y - lastY) / dt) * 1000;
      velocity = velocity * 0.8 + v * 0.2;
      lastY = y;
      lastT = now;
    };

    const updateOverlayHint = () => {
      const shouldHint = dragY > CLOSE_DIST;
      if (shouldHint === hintClosing) return;
      hintClosing = shouldHint;
      this.overlay = { state: shouldHint ? 'fade' : 'visible' };
    };

    const reset = () => {
      dragging = false;
      origin = null;
      committed = false;
      velocity = 0;
      dragY = 0;
      hintClosing = false;
      this.classList.remove('is-dragging');
      this.style.removeProperty('--mmd-y');
    };

    const inScrollBox = target => {
      for (let n = target; n && n !== this; n = n.parentElement) {
        if (!(n instanceof HTMLElement)) continue;
        const oy = getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return true;
      }
      return false;
    };

    const onDown = (e, from) => {
      if (e.touches.length !== 1) return;
      if (from === 'container') {
        if (!atTop()) return;
        if (performance.now() - lastScrollTime < TOP_COOLDOWN) return;
        if (inScrollBox(e.target)) return;
      }
      dragging = true;
      origin = from;
      committed = false;
      const { clientY } = e.touches[0];
      startY = lastY = clientY;
      lastT = performance.now();
      velocity = 0;
    };

    const onScroll = () => {
      const st = container.scrollTop;
      if (st !== lastScrollTop) {
        lastScrollTime = performance.now();
        lastScrollTop = st;
      }
    };

    const onMove = e => {
      if (!dragging || e.touches.length !== 1) return;
      const { clientY } = e.touches[0];
      const dy = clientY - startY;
      // Decide to takeover
      if (!committed) {
        if (origin === 'header' && dy <= 0) return setY(0);
        else if (origin === 'container' && !(atTop() && dy > TAKEOVER)) return setY(0);
        committed = true;
        this.classList.add('is-dragging');
      }
      if (e.cancelable) e.preventDefault();
      updateVelocity(clientY);
      const resisted = dy <= RESIST ? Math.max(0, dy) : RESIST + (dy - RESIST) * 0.75;
      setY(resisted);
      updateOverlayHint();
    };

    const onUp = () => {
      if (!dragging) return;
      if (dragY > CLOSE_DIST || velocity > CLOSE_VEL) this.close();
      else setY(0);
      reset();
    };

    const onCancel = () => {
      if (!dragging) return;
      setY(0);
      reset();
    };

    header.addEventListener('touchstart', e => onDown(e, 'header'), { passive: true });
    container.addEventListener('touchstart', e => onDown(e, 'container'), { passive: true });
    container.addEventListener('scroll', onScroll, { passive: true });
    this.addEventListener('touchmove', onMove, { passive: false });
    this.addEventListener('touchend', onUp, { passive: true });
    this.addEventListener('touchcancel', onCancel, { passive: true });
  }

  static get observedAttributes() {
    return ['open'];
  }

  /**
   * Set main overlay
   * @param {string} state - loading, visible or hidden
   * @param {Object} frame - documentElement or a modal parent
   */
  set overlay({ state, frame = this.frame }) {
    frame.dataset.modal = state;
  }

  /**
   * Set modal loader
   */
  set loading(state) {
    this.toggleAttribute('loading', state);
  }

  /**
   * Dispatch custom event
   * @param {Boolean} state - open or close
   */
  set event(state) {
    document.dispatchEvent(new CustomEvent(`maxdesign:modal:${state}`, { detail: this }));
  }

  set scroll({ xPos = 0, yPos = 0 }) {
    this.container.scrollTo({ top: xPos, left: yPos, behavior: 'smooth' });
  }

  set data({ id, modalTrigger, modalTitle, modalContent, ...data }) {
    this.details = { ...data };
    if (modalTitle) this.title = modalTitle;
    if (modalContent) this.content = modalContent;
    Object.assign(this.dataset, data);
  }

  set title(title) {
    this.querySelector('[slot="title"]').innerHTML = title;
  }

  set content(content) {
    this.querySelector('[slot="content"]').innerHTML = content;
  }

  /**
   * Get overlay frame
   * If modal is nested, return parent Modal
   */
  get frame() {
    return this.isConnected ? this.parentElement.closest('.mmd') || document.documentElement : this._frame;
  }

  get shadow() {
    return (this._shadow = this._shadow || !this.hasAttribute('data-light-dom'));
  }

  get isMobile() {
    return matchMedia('(max-width:440px)').matches;
  }
}
/* #endregion ═════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════════════ */
/* #region(collapsed) Register Elements */
customElements.define('modal-controller-maxdesign', ModalController);
customElements.define('modal-maxdesign', Modal);
/* #endregion ═════════════════════════════════════════════════════════════ */
